import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HeaderApp } from '../app/scaffold/HeaderApp';
import { getInitialData } from './getInitialData';
import { getPageExperiencesData } from './getPageExperiencesData';
import { isMin1024pxWide } from '../app/utils/device/identifyDevice';
import { isPreview } from '../lib/url/timestamp';
import type { ClientInitialState, ClientPreloadedState } from './types';

let isInitializing = false;
let hasInitialHydration = false;
let isObserverStarted = false;
let mountedRootEl: HTMLElement | null = null;

const loadInitialState = async (): Promise<void> => {
  if (isInitializing) return;

  let rootEl = document.getElementById('root');
  if (!rootEl || rootEl === mountedRootEl) return;

  isInitializing = true;

  try {
    const { host, search } = window.location;
    const params = new URLSearchParams(
      isPreview(host) && search.length ? search : ''
    );
    const windowPreloadedState =
      (window.__PRELOADED_STATE__ as ClientPreloadedState | undefined) || {};

    const exp = windowPreloadedState.exp;

    const premiumHeaderIsActive =
      windowPreloadedState.premiumHeaderIsActive === true;

    // get client state from combined /state and /config endpoints
    const initialState = await getInitialData(
      isMin1024pxWide(),
      params,
      exp || '',
      windowPreloadedState.headerClientConfigQueryParameters || '',
      premiumHeaderIsActive
    );

    rootEl = document.getElementById('root');
    if (!rootEl || rootEl === mountedRootEl) return;

    const experienceData = getPageExperiencesData();
    const features = {
      ...initialState.features,
      ...(windowPreloadedState.features || {}),
      ...(experienceData?.length && {
        monetateExperiences: experienceData,
      }),
    };
    const hasFeatures = Object.keys(features).length > 0;

    const preloadedState: ClientInitialState = {
      ...initialState,
      ...windowPreloadedState,
      ...(hasFeatures && {
        features,
      }),
    };

    if (hasInitialHydration) {
      createRoot(rootEl).render(<HeaderApp initialState={preloadedState} />);
    } else {
      hydrateRoot(rootEl, <HeaderApp initialState={preloadedState} />);
      hasInitialHydration = true;
    }

    mountedRootEl = rootEl;

    window.__PRELOADED_STATE__ = preloadedState;
  } catch (err) {
    console.error(`Error hydrating application ${err}`);
  } finally {
    isInitializing = false;
  }
};

const observeParentRemounts = (): void => {
  if (isObserverStarted) return;
  isObserverStarted = true;

  const observer = new MutationObserver(() => {
    const rootEl = document.getElementById('root');
    if (rootEl && rootEl !== mountedRootEl) {
      loadInitialState();
    }
  });

  observer.observe(document.body, { childList: true });
};

const bootstrap = (): void => {
  loadInitialState();
  observeParentRemounts();
};

if (process.env.WITH_SCAFFOLD === 'true') {
  // When running locally within a consuming app we need to check if DOM is already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
} else {
  document.addEventListener('DOMContentLoaded', bootstrap);
}
